import { FormEvent, useState } from "react";
import { Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

/** Stored verbatim with the signup as opt-in evidence — keep in sync with the label below. */
export const SIGNUP_CONSENT_TEXT =
  "I agree to receive emails from Troopn about launch updates and travel ideas. Unsubscribe anytime.";

export function SignupForm({
  onComplete,
}: {
  onComplete: () => void;
}) {
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [isDuplicate, setIsDuplicate] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "loading") return;

    if (!marketingConsent) {
      setStatus("error");
      setIsDuplicate(false);
      setErrorMessage("Please agree to receive emails from Troopn first.");
      return;
    }

    setStatus("loading");
    setErrorMessage("");
    setIsDuplicate(false);
    try {
      await apiRequest("POST", "/api/signups", {
        email: email.trim(),
        source: "funnel",
        marketingConsent: true,
        consentText: SIGNUP_CONSENT_TEXT,
      });
      onComplete();
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      const duplicate = message.includes("409");
      setStatus("error");
      setIsDuplicate(duplicate);
      setErrorMessage(
        duplicate
          ? "That email is already on the list. You can still set your preferences."
          : message.includes("400")
            ? "Enter a valid email address."
            : "We could not save that email. Please try again.",
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl bg-funnel-sand p-4 text-funnel-ink shadow-2xl shadow-black/20 sm:p-5" noValidate>
      <p className="mb-2 text-sm font-semibold">Start with your email</p>
      <p className="mb-4 text-sm leading-5 text-funnel-muted">
        Then we’ll help you set the travel preferences that make Troopn feel more personal.
      </p>
      <label htmlFor="signup-email" className="sr-only">Email address</label>
      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          id="signup-email"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (status === "error") setStatus("idle");
          }}
          aria-invalid={status === "error"}
          aria-describedby={status === "error" ? "signup-error" : undefined}
          placeholder="you@example.com"
          className="h-12 min-w-0 flex-1 rounded-xl border border-funnel-ink/20 bg-transparent px-4 text-base outline-none transition-shadow placeholder:text-funnel-muted focus:border-funnel-ink focus:ring-2 focus:ring-funnel-ink/15"
          data-testid="input-signup-email"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-funnel-coral px-5 text-sm font-bold text-white transition-colors hover:bg-funnel-coral-strong focus:outline-none focus:ring-2 focus:ring-funnel-ink focus:ring-offset-2 disabled:cursor-wait disabled:opacity-70"
          data-testid="button-signup-submit"
        >
          {status === "loading" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {status === "loading" ? "Saving" : "Set my travel preferences"}
        </button>
      </div>
      <label htmlFor="signup-consent" className="mt-4 flex items-start gap-3 text-sm leading-5 text-funnel-muted">
        <input
          id="signup-consent"
          name="marketingConsent"
          type="checkbox"
          checked={marketingConsent}
          onChange={(event) => {
            setMarketingConsent(event.target.checked);
            if (status === "error") setStatus("idle");
          }}
          className="mt-0.5 h-5 w-5 shrink-0 rounded border-funnel-ink/30 text-funnel-coral focus:ring-2 focus:ring-funnel-ink/30"
          data-testid="checkbox-signup-consent"
        />
        <span>
          {SIGNUP_CONSENT_TEXT}{" "}
          <a href="/privacy-policy" className="font-semibold text-funnel-ink underline underline-offset-2">
            Privacy Policy
          </a>
        </span>
      </label>
      {status === "error" && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p
            id="signup-error"
            className="text-sm font-medium text-funnel-error"
            role="alert"
            data-testid="text-signup-error"
          >
            {errorMessage}
          </p>
          {isDuplicate && (
            <button
              type="button"
              className="text-left text-sm font-bold text-funnel-ink underline underline-offset-4 sm:text-right"
              onClick={onComplete}
              data-testid="button-duplicate-continue"
            >
              Continue to preferences
            </button>
          )}
        </div>
      )}
    </form>
  );
}
